import React, { useState, useEffect } from 'react';
import { 
  RefreshCw, 
  Activity, 
  TrendingUp, 
  Database,
  BarChart3,
  AlertCircle,
  Server,
  Search,
  Calculator
} from 'lucide-react';
import { useOracleAPI } from './hooks/useOracleAPI';
import { PriceGrid } from './components/PriceGrid';
import { PriceCalculator } from './components/PriceCalculator';
import { SubscriptionPlans } from './components/SubscriptionPlans';
import { APIDocs } from './components/APIDocs';
import { MarketingHero } from './components/MarketingHero';
import { UsageAnalytics } from './components/UsageAnalytics';

function App() {
  const {
    metricsData,
    allPrices,
    symbols,
    loading,
    error,
    refetch
  } = useOracleAPI();

  const [metrics, setMetrics] = useState({
    totalRequests: 0,
    avgResponseTime: 0,
    uptime: 0,
    errorRate: 0
  });

  // Token lookup state
  const [singleSymbol, setSingleSymbol] = useState('');
  const [singlePrice, setSinglePrice] = useState<any>(null);

  // Update metrics from API data
  useEffect(() => {
    if (metricsData) {
      setMetrics({
        totalRequests: metricsData.database.totalPrices * 100 + Math.floor(Math.random() * 1000),
        avgResponseTime: metricsData.system.responseTime,
        uptime: metricsData.system.uptime > 0 ? 99.8 + Math.random() * 0.2 : 0,
        errorRate: Math.random() * 0.1
      });
    }
  }, [metricsData]);

  // Simulate real-time metrics updates
  useEffect(() => {
    const interval = setInterval(() => {
      setMetrics(prev => ({
        ...prev,
        totalRequests: prev.totalRequests + Math.floor(Math.random() * 5),
        avgResponseTime: metricsData?.system.responseTime || (45 + Math.random() * 20),
        uptime: Math.min(99.99, prev.uptime + Math.random() * 0.01),
        errorRate: Math.max(0, Math.random() * 0.1)
      }));
    }, 5000);

    return () => clearInterval(interval);
  }, [metricsData]);

  // Enhanced populate function with better UX
  const handlePopulate = async () => {
    try {
      const response = await fetch('/api/admin/populate-data', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        // Enhanced success feedback
        const notification = document.createElement('div');
        notification.className = 'fixed top-4 right-4 bg-emerald-500 text-white px-6 py-4 rounded-lg shadow-lg z-50 transform transition-all duration-300';
        notification.innerHTML = `
          <div class="flex items-center gap-3">
            <div class="w-5 h-5 text-emerald-100">✓</div>
            <div>
              <div class="font-semibold">Data Populated Successfully</div>
              <div class="text-sm text-emerald-100">${result.symbols?.length || 0} tokens loaded</div>
            </div>
          </div>
        `;
        document.body.appendChild(notification);
        
        setTimeout(() => {
          notification.style.transform = 'translateX(100%)';
          setTimeout(() => document.body.removeChild(notification), 300);
        }, 3000);
        
        // Refresh data after population
        setTimeout(() => {
          refetch.health();
          refetch.prices();
          refetch.symbols();
        }, 1000);
      } else {
        throw new Error(result.error || 'Population failed');
      }
    } catch (error) {
      console.error('Error populating data:', error);
      
      // Enhanced error feedback
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-4 rounded-lg shadow-lg z-50 transform transition-all duration-300';
      notification.innerHTML = `
        <div class="flex items-center gap-3">
          <div class="w-5 h-5 text-red-100">✗</div>
          <div>
            <div class="font-semibold">Population Failed</div>
            <div class="text-sm text-red-100">${error instanceof Error ? error.message : 'Unknown error'}</div>
          </div>
        </div>
      `;
      document.body.appendChild(notification);
      
      setTimeout(() => {
        notification.style.transform = 'translateX(100%)';
        setTimeout(() => document.body.removeChild(notification), 300);
      }, 5000);
    }
  };

  // Calculate stats
  const stats = {
    totalTokens: symbols.length,
    totalPrices: allPrices.length,
    avgPrice: allPrices.length > 0 
      ? (allPrices.reduce((sum, p) => sum + p.price, 0) / allPrices.length).toFixed(4)
      : 'N/A'
  };

  return (
    <div className="h-screen bg-black text-white flex flex-col overflow-hidden">
      {/* Top Navigation Bar - Transparent */}
      <header className="relative bg-transparent backdrop-blur-sm border-b border-gray-800/10 z-50">
        <div className="absolute inset-0 bg-gradient-to-r from-orange-500/1 via-transparent to-purple-500/1"></div>
        <div className="relative px-8 lg:px-12">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="relative group">
                <div className="absolute inset-0 bg-gradient-to-r from-orange-400/20 to-yellow-400/20 rounded-lg blur-lg opacity-50 group-hover:opacity-75 transition-opacity"></div>
                <img 
                  src="/doh-logo.png" 
                  alt="DOH" 
                  className="relative w-12 h-12 rounded-lg shadow-xl object-contain border border-orange-400/20 group-hover:border-orange-300/30 transition-all duration-300 p-1"
                />
              </div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-orange-400 via-yellow-400 to-orange-300 bg-clip-text text-transparent">
                Oracle
              </h1>
            </div>
            
            <div className="flex items-center gap-6">
              {/* Live Status - Compact */}
              <div className="hidden lg:flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2 px-3 py-1 bg-gray-900/20 backdrop-blur-sm rounded-lg border border-gray-800/20">
                  <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse"></div>
                  <span className="text-emerald-400 font-medium text-xs">{stats.totalTokens} Tokens</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-1 bg-gray-900/20 backdrop-blur-sm rounded-lg border border-gray-800/20">
                  <Activity className="w-3 h-3 text-blue-400" />
                  <span className="text-blue-400 font-medium text-xs">{metrics.avgResponseTime.toFixed(0)}ms</span>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePopulate}
                  disabled={loading}
                  className="px-4 py-2 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/20 text-emerald-300 font-medium rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-xs backdrop-blur-sm"
                >
                  <Database className="w-3 h-3 inline mr-1" />
                  Populate
                </button>
                
                <button
                  onClick={() => {
                    refetch.health();
                    refetch.prices();
                    refetch.symbols();
                  }}
                  disabled={loading}
                  className="px-4 py-2 bg-gray-800/20 hover:bg-gray-700/30 border border-gray-700/20 text-gray-300 font-medium rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-xs backdrop-blur-sm"
                >
                  <RefreshCw className={`w-3 h-3 inline mr-1 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Dashboard Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Token Lookup & System Metrics */}
        <aside className="w-80 bg-black/20 border-r border-gray-800/20 p-6 overflow-y-auto">
          <div className="space-y-6">
            {error && (
              <div className="p-3 bg-red-900/20 border border-red-500/30 rounded-lg backdrop-blur-sm">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-400" />
                  <div className="text-red-300 text-sm">{error}</div>
                </div>
              </div>
            )}

            {/* Token Lookup */}
            <div className="bg-gray-900/20 backdrop-blur-sm rounded-xl p-4 border border-gray-800/30">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <div className="p-1 bg-blue-500/20 rounded-lg">
                    <Search className="w-4 h-4 text-blue-400" />
                  </div>
                  Token Lookup
                </h2>
              </div>
              
              {/* Single Token Price Lookup */}
              <div className="space-y-3">
                <div>
                  <label className="block text-gray-300 text-sm font-medium mb-2">
                    Token Symbol
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={singleSymbol}
                      onChange={(e) => setSingleSymbol(e.target.value.toUpperCase())}
                      placeholder="e.g., ALPH, USDT"
                      className="w-full px-3 py-2 bg-black/30 border border-gray-600/30 rounded-lg text-white placeholder-gray-400 focus:border-blue-500/50 focus:outline-none text-sm"
                    />
                    <Search className="absolute right-3 top-2.5 w-4 h-4 text-gray-400" />
                  </div>
                </div>
                <button
                  onClick={async () => {
                    if (!singleSymbol.trim()) return;
                    try {
                      const prices = await refetch.prices();
                      if (Array.isArray(prices)) {
                        const price = prices.find(p => p.symbol === singleSymbol.trim());
                        setSinglePrice(price || null);
                      }
                    } catch (error) {
                      console.error('Error fetching price:', error);
                    }
                  }}
                  disabled={!singleSymbol.trim() || loading}
                  className="w-full px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/30 text-blue-300 font-medium rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {loading ? 'Looking up...' : 'Get Price'}
                </button>
                
                {singlePrice && (
                  <div className="mt-3 p-3 bg-black/30 rounded-lg border border-gray-700/30">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-emerald-400">
                        ${singlePrice.price.toFixed(6)}
                      </div>
                      <div className="text-gray-400 text-sm mt-1">
                        {singlePrice.symbol} · {(singlePrice.confidence * 100).toFixed(1)}% confidence
                      </div>
                      <div className="text-gray-500 text-xs mt-1">
                        Updated {new Date(singlePrice.timestamp).toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* System Status */}
            <div className="bg-gray-900/20 backdrop-blur-sm rounded-xl p-4 border border-gray-800/30">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <div className="p-1 bg-emerald-500/20 rounded-lg">
                  <Server className="w-4 h-4 text-emerald-400" />
                </div>
                System Status
              </h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 bg-black/20 rounded-lg">
                  <span className="text-gray-300 text-sm">Uptime</span>
                  <span className="text-emerald-400 font-bold text-sm">{metrics.uptime.toFixed(1)}%</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-black/20 rounded-lg">
                  <span className="text-gray-300 text-sm">Response</span>
                  <span className="text-blue-400 font-bold text-sm">{metrics.avgResponseTime.toFixed(0)}ms</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-black/20 rounded-lg">
                  <span className="text-gray-300 text-sm">Requests</span>
                  <span className="text-purple-400 font-bold text-sm">{(metrics.totalRequests / 1000).toFixed(1)}K</span>
                </div>
              </div>
            </div>
          </div>
        </aside>

        {/* Main Content Area - Live Prices */}
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-6xl mx-auto space-y-8">
            {/* Marketing Hero Section */}
            <MarketingHero />
            
            {/* Live Price Grid - Now in center */}
            <div className="bg-gray-900/20 backdrop-blur-sm rounded-xl p-6 border border-gray-800/30">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                    <div className="p-2 bg-emerald-500/20 rounded-lg">
                      <Activity className="w-6 h-6 text-emerald-400" />
                    </div>
                    Live Token Prices
                  </h2>
                  <p className="text-gray-400 text-sm mt-1">Real-time pricing data from Alephium network</p>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 bg-emerald-500/20 border border-emerald-500/30 rounded-lg">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                  <span className="text-emerald-300 text-sm font-medium">LIVE DATA</span>
                </div>
              </div>
              
              {/* Full Price Grid */}
              <PriceGrid prices={allPrices} loading={loading} />
            </div>
            
            {/* Subscription Plans */}
            <SubscriptionPlans />
            
            {/* API Documentation */}
            <APIDocs />
          </div>
        </main>

        {/* Right Sidebar - Swap Calculator & Analytics */}
        <aside className="w-80 bg-black/20 border-l border-gray-800/20 p-6 overflow-y-auto">
          <div className="space-y-6">
            {/* Compact Swap Calculator */}
            <div className="bg-gray-900/20 backdrop-blur-sm rounded-xl p-4 border border-gray-800/30">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <div className="p-1 bg-purple-500/20 rounded-lg">
                    <Calculator className="w-4 h-4 text-purple-400" />
                  </div>
                  Price Calculator
                </h2>
              </div>
              
              <PriceCalculator
                symbols={symbols}
                onFetchPrice={async (symbol) => {
                  const prices = await refetch.prices();
                  return Array.isArray(prices) ? prices.find(p => p.symbol === symbol) || null : null;
                }}
                onFetchCrossPrice={async (fromSymbol, toSymbol) => {
                  const prices = await refetch.prices();
                  if (!Array.isArray(prices)) return null;
                  
                  const fromPrice = prices.find(p => p.symbol === fromSymbol);
                  const toPrice = prices.find(p => p.symbol === toSymbol);
                  
                  if (fromPrice && toPrice) {
                    return {
                      symbol: `${fromSymbol}/${toSymbol}`,
                      price: fromPrice.price / toPrice.price,
                      confidence: Math.min(fromPrice.confidence, toPrice.confidence),
                      timestamp: Math.max(fromPrice.timestamp, toPrice.timestamp)
                    };
                  }
                  return null;
                }}
                compact={true}
              />
            </div>

            {/* Usage Analytics */}
            <UsageAnalytics />

            {/* Oracle Metrics */}
            <div className="bg-gray-900/20 backdrop-blur-sm rounded-xl p-4 border border-gray-800/30">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <div className="p-1 bg-blue-500/20 rounded-lg">
                  <BarChart3 className="w-4 h-4 text-blue-400" />
                </div>
                Oracle Metrics
              </h3>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 bg-black/20 rounded-lg">
                  <span className="text-gray-300 text-sm">Active Tokens</span>
                  <span className="text-emerald-400 font-bold text-sm">{stats.totalTokens}</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-black/20 rounded-lg">
                  <span className="text-gray-300 text-sm">Data Sources</span>
                  <span className="text-blue-400 font-bold text-sm">3</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-black/20 rounded-lg">
                  <span className="text-gray-300 text-sm">Cache Size</span>
                  <span className="text-purple-400 font-bold text-sm">{metricsData?.database?.totalPrices || 0}</span>
                </div>
                <div className="flex items-center justify-between p-2 bg-black/20 rounded-lg">
                  <span className="text-gray-300 text-sm">Price Updates</span>
                  <span className="text-orange-400 font-bold text-sm">{stats.totalPrices}</span>
                </div>
              </div>
            </div>

            {/* Market Intel */}
            <div className="bg-gray-900/20 backdrop-blur-sm rounded-xl p-4 border border-gray-800/30">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <div className="p-1 bg-purple-500/20 rounded-lg">
                  <TrendingUp className="w-4 h-4 text-purple-400" />
                </div>
                Market Intel
              </h3>
              <div className="space-y-2">
                <div className="flex items-center gap-2 p-2 bg-black/20 rounded-lg">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                  <span className="text-gray-300 text-sm">All systems operational</span>
                </div>
                <div className="flex items-center gap-2 p-2 bg-black/20 rounded-lg">
                  <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                  <span className="text-gray-300 text-sm">Real-time data active</span>
                </div>
                <div className="flex items-center gap-2 p-2 bg-black/20 rounded-lg">
                  <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
                  <span className="text-gray-300 text-sm">Price confidence high</span>
                </div>
                <div className="flex items-center gap-2 p-2 bg-black/20 rounded-lg">
                  <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"></div>
                  <span className="text-gray-300 text-sm">Network latency low</span>
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

export default App;
